import { ISavedColor } from "@kuika/kuika-cl-model";
import { IScreenDesignContextState } from "../../contexts/screen-design-context/screen-design-context";
import type { ScreenDesignInstance } from "./designer-context";
import { getValues } from "./designer-context";

interface IColorSummary {
  id: string;
  name: string;
  color: string;
  isDefault: boolean;
}

interface IListColorsResult {
  count: number;
  colors: IColorSummary[];
}

interface IColorMutationSuccess {
  success: true;
  message: string;
  color: { id: string; name: string; color: string };
}

interface IColorMutationError {
  error: string;
}

interface IDeleteColorSuccess {
  success: true;
  message: string;
}

function getProjectColorsProvider(sd: IScreenDesignContextState): IScreenDesignContextState {
  return sd;
}

function getAllSavedColors(sd: IScreenDesignContextState): ISavedColor[] {
  return sd?.getAllSavedColors?.() || [];
}

export function listDesignerProjectColors(sdInstance: ScreenDesignInstance): IListColorsResult {
  const colors = getAllSavedColors(getValues(sdInstance));
  return {
    count: colors.length,
    colors: colors.map((color: ISavedColor) => ({
      id: color.id,
      name: color.name,
      color: color.color,
      isDefault: color.isDefault || false
    }))
  };
}

export function addDesignerProjectColor(
  sdInstance: ScreenDesignInstance,
  input: { name?: string; color?: string; id?: string }
): IColorMutationSuccess | IColorMutationError {
  const { name, color } = input;
  if (!name) return { error: "name is required" };
  if (!color) return { error: "color is required" };

  const sd = getValues(sdInstance);
  const existing = getAllSavedColors(sd);
  if (existing.some((item: ISavedColor) => item.name === name)) {
    return { error: `A color named "${name}" already exists` };
  }

  const id = input.id || crypto.randomUUID();
  getProjectColorsProvider(sd).addSavedColor(id, name, color);

  return {
    success: true,
    message: `Added project color "${name}" (${color})`,
    color: { id, name, color }
  };
}

export async function updateDesignerProjectColor(
  sdInstance: ScreenDesignInstance,
  input: { id?: string; name?: string; color?: string }
): Promise<IColorMutationSuccess | IColorMutationError> {
  const { id, name, color } = input;
  if (!id) return { error: "id is required" };
  if (!name && !color) return { error: "At least one of name or color must be provided" };

  const sd = getValues(sdInstance);
  const existing = getAllSavedColors(sd);
  const target = existing.find((item: ISavedColor) => item.id === id);
  if (!target) return { error: `Project color with id "${id}" not found` };

  const updatedName = name || target.name;
  const updatedColor = color || target.color;
  getProjectColorsProvider(sd).updateSavedColor(id, updatedName, updatedColor);

  return {
    success: true,
    message: `Updated project color "${updatedName}" (${updatedColor})`,
    color: { id, name: updatedName, color: updatedColor }
  };
}

export function deleteDesignerProjectColor(
  sdInstance: ScreenDesignInstance,
  id?: string
): IDeleteColorSuccess | IColorMutationError {
  if (!id) return { error: "id is required" };

  const sd = getValues(sdInstance);
  const existing = getAllSavedColors(sd);
  const target = existing.find((item: ISavedColor) => item.id === id);
  if (!target) return { error: `Project color with id "${id}" not found` };

  getProjectColorsProvider(sd).deleteSavedColor(id);
  return {
    success: true,
    message: `Deleted project color "${target.name}"`
  };
}
